import { CommonModule } from '@angular/common';
import {
	Component,
	inject,
	Inject,
	ChangeDetectionStrategy,
	OnInit,
	signal,
	computed
} from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';
import { ProfileService } from '../../services/profile.service';
import { SelectCheckboxComponent } from '@app/shared/select-checkbox/select-checkbox.component';
import { ToastrService } from 'ngx-toastr';

@Component({
	selector: 'app-paytype-weekday-dialog',
	imports: [
		CommonModule,
		MaterialModule,
		FormsModule,
		ReactiveFormsModule,
		SelectCheckboxComponent
	],
	templateUrl: './paytype-weekday-dialog.component.html',
	styleUrl: './paytype-weekday-dialog.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaytypeWeekdayDialogComponent implements OnInit {
	paytypeForm!: FormGroup;

	// Signals for state management
	isLoading = signal<boolean>(false);
	allPaytypes = signal<any[]>([]);
	selectedPaytypes = signal<any[]>([]);
	dayName = signal<string>('');
	dayNo = signal<number>(0);

	private readonly profileService: ProfileService = inject(ProfileService);
	private readonly toastr: ToastrService = inject(ToastrService);

	constructor(
		private readonly fb: FormBuilder,
		@Inject(MAT_DIALOG_DATA) public data: any,
		public dialogRef: MatDialogRef<PaytypeWeekdayDialogComponent>
	) {
		this.dayName.set(data.dayName);
		this.dayNo.set(data.dayNo);
		this.paytypeForm = this.fb.group({
			PayTypes: [[]]
		});
	}

	ngOnInit(): void {
		this.loadPaytypes();
	}

	loadPaytypes(): void {
		this.isLoading.set(true);
		this.profileService.getAllPayTypes().subscribe({
			next: (response) => {
				const paytypes = response.map((item: any) => ({
					value: item.PayType,
					label: item.PayType
				}));
				this.allPaytypes.set(paytypes);
				// After paytypes are loaded, load existing restrictions
				this.loadExistingPaytypes();
			},
			error: (error) => {
				console.log('Error loading paytypes:', error);
				this.toastr.error('Failed to load paytypes', 'Error');
				this.isLoading.set(false);
			}
		});
	}

	loadExistingPaytypes(): void {
		this.profileService.getPaytypeWeekdayRestrictedList().subscribe({
			next: (response) => {
				const dayData = response.find(
					(item: any) => item.DayNo === this.dayNo()
				);
				if (dayData && dayData.PayType) {
					// Parse comma-separated paytypes
					const paytypeArray = dayData.PayType.split(',').map(
						(pt: string) => pt.trim()
					);
					// Map to the format expected by select-checkbox component
					const selected = this.allPaytypes().filter((pt: any) =>
						paytypeArray.includes(pt.value)
					);
					this.selectedPaytypes.set(selected);
					this.paytypeForm.patchValue({
						PayTypes: selected
					});
				}
				this.isLoading.set(false);
			},
			error: (error) => {
				console.log('Error loading existing paytypes:', error);
				this.isLoading.set(false);
			}
		});
	}

	onSave(): void {
		const selectedPaytypes = this.paytypeForm.get('PayTypes')?.value || [];

		if (selectedPaytypes.length === 0) {
			// If no paytypes selected, send empty string or null
			this.savePaytypes('');
		} else {
			// Convert array of objects to comma-separated string
			const paytypeString = selectedPaytypes
				.map((pt: any) => (pt.value ? pt.value : pt))
				.join(',');
			this.savePaytypes(paytypeString);
		}
	}

	savePaytypes(paytypeString: string): void {
		const payload = {
			DayNo: this.dayNo(),
			PayType: paytypeString || ''
		};

		this.profileService.savePaytypeWeekdayRestricted(payload).subscribe({
			next: (response) => {
				this.toastr.success(
					`Paytypes saved successfully for ${this.dayName()}`,
					'Success',
					{
						positionClass: 'toast-bottom-left',
						toastClass: 'toast-icon custom-toast-success'
					}
				);
				this.dialogRef.close('saved');
			},
			error: (error) => {
				console.log('Error saving paytypes:', error);
				this.toastr.error('Failed to save paytypes', 'Error');
			}
		});
	}

	closeDialog(): void {
		this.dialogRef.close();
	}
}
